/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { Plus } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { useState } from 'react'

import { AddToBasketWrapper } from '@/components/e-commerce/basket/AddToBasketWrapper'
import { KitVariantDropdown, MtaKitDropdownVariant } from '@/components/e-commerce/kits/KitVariantDropdown'
import { Button } from '@/components/ui/Button'
import { NumberInput } from '@/components/ui/NumberInput'
import { useKitSlug } from '@/hooks/kits/useKitSlug'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type KitAddToBasketProps = {
  /** Additional variants to provide in the dropdown. */
  variants?: Array<MtaKitDropdownVariant>
}

/**
 * The add to basket element for an MTa Kit product page. Provides the user with the ability to switch the kit variant
 * they're purchasing, as well as quantity.
 */
const KitAddToBasket: React.FC<KitAddToBasketProps> = ({ variants = [] }) => {
  const t = useTranslations('KitAddToBasket')
  const [selectedVariant, setSelectedVariant] = useState<string | undefined>(undefined)
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1)
  const kitSlug = useKitSlug()

  return (
    <div className={'flex flex-col gap-2'}>
      <KitVariantDropdown variants={variants} onVariantChange={(variant) => setSelectedVariant(variant)} />
      <div className={'flex flex-row items-center gap-6'}>
        <NumberInput onChange={(quantity) => setSelectedQuantity(quantity)} />
        {kitSlug && (
          <AddToBasketWrapper
            item={{
              productId: kitSlug,
              productType: 'kit',
              quantity: selectedQuantity,
              variantId: selectedVariant,
            }}
          >
            <Button
              icon={<Plus />}
              iconPosition={'left'}
              type={'button'}
              rounded={'full'}
              className={cn('py-3 px-5', 'mta-add-to-cart')}
            >
              {t('addToBasket')}
            </Button>
          </AddToBasketWrapper>
        )}
      </div>
    </div>
  )
}

export { KitAddToBasket }
